import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css'; // Importar os estilos do toast
import { UserProfile, UserConnection, UserGame, UserMessage } from '../types';
import CommunityService from '../lib/services/communityService';
import { useAuth } from '../contexts/AuthContext';
import { Spinner } from './Spinner';
// Importar funções necessárias para interação com jogos
import { toggleOwned, toggleWishlist, togglePlayed } from '../lib/gameService';
import { rateGame } from '../lib/game/ratingService';
import { updatePlayCount, getUserCollectionStatusIds } from '../lib/game/collectionService';
import { GameCard } from './GameCard';
import { supabase } from '../lib/supabase'; // Ajuste o caminho
import SEO from './SEO';
import FeedComponent from './feed/FeedComponent'; // Importar o FeedComponent
import { storageService } from '../lib/services/storageService'; // Importar storageService
import { Edit, Save, XCircle, Upload, MessageSquare, UserPlus, UserCheck, UserX, History, Award, Star, Hash, BarChart, Loader2, PlusCircle, CheckCircle, Heart, Settings2, Eye, Trash2, ThumbsUp, ThumbsDown, StarHalf, BookOpen } from 'lucide-react'; // Importar ícones (BookOpen adicionado)
import { Button } from '@/components/ui/button'; // Importar Button do Shadcn
import { Badge as UiBadge } from '@/components/ui/badge'; // Importar Badge do Shadcn
import { Progress } from '@/components/ui/progress'; // Importar Progress do Shadcn
import { Skeleton } from '@/components/ui/skeleton'; // Importar Skeleton
// Adicionar imports de gamificação
import { UserXpData } from '../lib/gamification/types'; 
import { getUserGamificationStatus } from '../lib/gamification/gamificationService';
import { calculateLevelProgress } from '../lib/gamification/gamificationUtils';
import { getPublicProfileBadges, type UserProfileBadge } from '../lib/gamification/badgeService'; // <<< Importar função e tipo
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"; // <<< Importar Tooltip
import { getUserMascot } from '../lib/gamification/mascotService';
import { UserMascot } from '../lib/gamification/mascotService';
import { useTheme } from '../contexts/ThemeContext'; // <<< Adicionar import do useTheme
import { getUserReviews } from '../lib/game/reviewService'; 

type ProfileTabType = 'collection' | 'wishlist' | 'played' | 'rated';

// Criar uma instância do serviço
const communityService = new CommunityService();

const INITIAL_GAMES_LIMIT = 5;
const GAMES_LOAD_MORE_INCREMENT = 10;

const PublicProfilePage: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const { user, updateProfile } = useAuth();
  const navigate = useNavigate();
  const { theme } = useTheme(); // <<< Obter o tema atual
  
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [activeTab, setActiveTab] = useState<ProfileTabType>('collection');
  const [games, setGames] = useState<UserGame[]>([]);
  const [allGamesForCurrentTab, setAllGamesForCurrentTab] = useState<UserGame[]>([]);
  const [visibleGamesCount, setVisibleGamesCount] = useState<number>(INITIAL_GAMES_LIMIT);
  const [loading, setLoading] = useState(true);
  const [gamesLoading, setGamesLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<UserConnection | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [userGameStatusIds, setUserGameStatusIds] = useState<{
    ownedIds: Set<string>;
    wishlistIds: Set<string>;
    playedIds: Set<string>;
  }>({
    ownedIds: new Set(),
    wishlistIds: new Set(),
    playedIds: new Set(),
  });
  const [error, setError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [userGames, setUserGames] = useState<UserGame[]>([]);
  const [userCollectionStatus, setUserCollectionStatus] = useState<{
    owned: Set<string>;
    wishlist: Set<string>;
    played: Set<string>;
  }>({ owned: new Set(), wishlist: new Set(), played: new Set() });
  
  // Estado para edição de perfil
  const [isEditingBio, setIsEditingBio] = useState(false);
  const [newBio, setNewBio] = useState('');
  const [isSavingBio, setIsSavingBio] = useState(false);
  const [isModeratingBio, setIsModeratingBio] = useState(false); // Estado para moderação da bio
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [isModeratingAvatar, setIsModeratingAvatar] = useState(false); // Estado para moderação do avatar
  const avatarInputRef = useRef<HTMLInputElement>(null);

  // <<< NOVO: Estados para gamificação do perfil >>>
  const [profileStatus, setProfileStatus] = useState<UserXpData | null>(null);
  const [loadingProfileStatus, setLoadingProfileStatus] = useState(false);
  const [publicBadges, setPublicBadges] = useState<UserProfileBadge[]>([]); // <<< NOVO: Estado para badges públicas
  const [loadingPublicBadges, setLoadingPublicBadges] = useState(false); // <<< NOVO: Estado de loading para badges
  const [userMascot, setUserMascot] = useState<UserMascot | null>(null);
  const [loadingMascot, setLoadingMascot] = useState(true);

  const isOwnProfile = user && profile && user.id === profile.id;

  // <<< NOVO: Handler para clique em tag no feed público >>>
  const handlePublicFeedTagClick = (type: string, value: string) => {
    console.log(`[PublicProfilePage] Clicou na tag: ${type} = ${value}. Navegando para busca...`);
    // Navega para a página de busca com o filtro aplicado
    navigate(`/search?tagType=${type}&tagValue=${value}`);
  };

  // Handlers para as ações dos GameCards
  const handleToggleOwned = async (gameId: string, isOwned: boolean) => {
    if (!user) {
      toast.error('Você precisa estar logado para adicionar jogos à sua coleção');
      return;
    }
    
    try {
      await toggleOwned(user.id, gameId, isOwned);
      // Atualizar o estado local dos jogos do usuário
      setUserGameStatusIds(prev => {
        const newOwnedIds = new Set(prev.ownedIds);
        if (isOwned) {
          newOwnedIds.add(gameId);
        } else {
          newOwnedIds.delete(gameId);
        }
        return { ...prev, ownedIds: newOwnedIds };
      });
      toast.success(isOwned ? 'Jogo adicionado à sua coleção' : 'Jogo removido da sua coleção');
    } catch (error) {
      console.error('Erro ao atualizar propriedade owned:', error);
      toast.error('Não foi possível atualizar o jogo. Tente novamente.');
    }
  };

  const handleToggleWishlist = async (gameId: string, isWishlist: boolean) => {
    if (!user) {
      toast.error('Você precisa estar logado para adicionar jogos à sua lista de desejos');
      return;
    }
    
    try {
      await toggleWishlist(user.id, gameId, isWishlist);
      // Atualizar o estado local dos jogos do usuário
      setUserGameStatusIds(prev => {
        const newWishlistIds = new Set(prev.wishlistIds);
        if (isWishlist) {
          newWishlistIds.add(gameId);
        } else {
          newWishlistIds.delete(gameId);
        }
        return { ...prev, wishlistIds: newWishlistIds };
      });
      toast.success(isWishlist ? 'Jogo adicionado à sua lista de desejos' : 'Jogo removido da sua lista de desejos');
    } catch (error) {
      console.error('Erro ao atualizar wishlist:', error);
      toast.error('Não foi possível atualizar o jogo. Tente novamente.');
    }
  };

  const handleTogglePlayed = async (gameId: string, isPlayed: boolean) => {
    if (!user) {
      toast.error('Você precisa estar logado para marcar jogos como jogados');
      return;
    }
    
    try {
      await togglePlayed(user.id, gameId, isPlayed);
      
      // Log de debug para verificar o ID do jogo
      console.log(`Alterando status de jogado: ID ${gameId}, isPlayed: ${isPlayed}`);
      
      // Atualizar o estado local dos jogos do usuário
      setUserGameStatusIds(prev => {
        const newPlayedIds = new Set(prev.playedIds);
        if (isPlayed) {
          newPlayedIds.add(gameId);
        } else {
          newPlayedIds.delete(gameId);
        }
        
        // Log de debug para verificar o conjunto após atualização
        console.log(`Played IDs após atualização: ${Array.from(newPlayedIds).join(', ')}`);
        
        return { ...prev, playedIds: newPlayedIds };
      });
      
      toast.success(isPlayed ? 'Jogo marcado como jogado' : 'Jogo desmarcado como jogado');
    } catch (error) {
      console.error('Erro ao marcar jogo como jogado:', error);
      toast.error('Não foi possível atualizar o status de jogado. Tente novamente.');
    }
  };

  const handleRateGame = async (gameId: string, rating: number) => {
    if (!user) {
      toast.error('Você precisa estar logado para avaliar jogos');
      return;
    }
    
    try {
      await rateGame(user.id, gameId, rating);
      toast.success(`Jogo avaliado com ${rating} estrelas`);
      // Recarregar status dos jogos para manter a interface atualizada
      if (user) {
        const statusIds = await getUserCollectionStatusIds(user.id);
        setUserGameStatusIds(statusIds);
      }
    } catch (error) {
      console.error('Erro ao avaliar jogo:', error);
      toast.error('Não foi possível avaliar o jogo. Tente novamente.');
    }
  };

  const handleUpdatePlayCount = async (gameId: string, playCount: number) => {
    if (!user) {
      toast.error('Você precisa estar logado para atualizar contagem de jogadas');
      return;
    }
    
    try {
      await updatePlayCount(user.id, gameId, playCount);
      // Atualizar o estado local dos jogos do usuário
      setUserGameStatusIds(prev => {
        const newPlayedIds = new Set(prev.playedIds);
        if (playCount > 0) {
          newPlayedIds.add(gameId);
        } else {
          newPlayedIds.delete(gameId);
        }
        return { ...prev, playedIds: newPlayedIds };
      });
      toast.success(`Contagem de jogadas atualizada para ${playCount}`);
    } catch (error) {
      console.error('Erro ao atualizar contagem de jogadas:', error);
      toast.error('Não foi possível atualizar a contagem de jogadas. Tente novamente.');
    }
  };

  // Buscar perfil do usuário pelo username
  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      setLoadingProfileStatus(true); // Inicia carregamento do status
      setLoadingPublicBadges(true); // <<< Inicia carregamento das badges
      setError(null);
      setProfile(null); // Limpa perfil anterior
      setProfileStatus(null); // Limpa status anterior
      setPublicBadges([]); // <<< Limpa badges anteriores

      if (!username) {
        setError('Nome de usuário não especificado.');
        setLoading(false);
        setLoadingProfileStatus(false);
        setLoadingPublicBadges(false); // <<< Finaliza loading
        navigate('/');
        return;
      }

      try {
        const { data, error: fetchError } = await supabase
          .from('profiles')
          .select('*')
          .eq('username', username)
          .single();

        if (fetchError) {
          if (fetchError.code === 'PGRST116') {
            setError('Perfil não encontrado.');
          } else {
            throw fetchError;
          }
          setLoading(false);
          setLoadingProfileStatus(false);
          setLoadingPublicBadges(false); // <<< Finaliza loading em erro
          return; // <<< Importante: retornar aqui
        }
        
        // Perfil encontrado
        const fetchedProfile = data as UserProfile;
        setProfile(fetchedProfile);

        // <<< Buscar dados de gamificação e badges em paralelo >>>
        const gamificationPromise = getUserGamificationStatus(supabase, fetchedProfile.id)
          .then(statusData => setProfileStatus(statusData))
          .catch(statusError => {
            console.error("Erro ao buscar status de gamificação:", statusError);
            // Não definir erro geral, apenas logar
          })
          .finally(() => setLoadingProfileStatus(false));

        const badgesPromise = getPublicProfileBadges(fetchedProfile.id)
          .then(badgesData => setPublicBadges(badgesData))
          .catch(badgesError => {
            console.error("Erro ao buscar badges públicas:", badgesError);
            toast.error("Não foi possível carregar as badges do perfil.");
          })
          .finally(() => setLoadingPublicBadges(false));

        // Verificar status da conexão (se logado)
        let connectionPromise = Promise.resolve(); // Promessa resolvida por padrão
        if (user && user.id !== fetchedProfile.id) {
          connectionPromise = communityService.getConnectionStatus(user.id, fetchedProfile.id)
            .then(connection => setConnectionStatus(connection))
            .catch(connError => console.error("Erro ao buscar status de conexão:", connError));
        }

        // Esperar todas as buscas paralelas terminarem
        await Promise.all([gamificationPromise, badgesPromise, connectionPromise]);

      } catch (err: any) {
        console.error("Erro ao buscar perfil público:", err);
        setError('Ocorreu um erro ao carregar o perfil.');
        setLoadingProfileStatus(false);
        setLoadingPublicBadges(false);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [username, user, navigate]); // Dependências

  // Buscar status dos jogos para o usuário atual (logado)
  useEffect(() => {
    const fetchUserGameStatus = async () => {
      if (!user) return;
      
      try {
        console.log(`Buscando status dos jogos para o usuário ${user.id}...`);
        const statusIds = await getUserCollectionStatusIds(user.id);
        
        // Log para debug - mostrando quantos jogos estão em cada categoria
        console.log(`Status carregados: 
          Owned: ${statusIds.ownedIds.size} jogos, 
          Wishlist: ${statusIds.wishlistIds.size} jogos, 
          Played: ${statusIds.playedIds.size} jogos`);
        
        setUserGameStatusIds(statusIds);
      } catch (error) {
        console.error('Erro ao buscar status dos jogos do usuário:', error);
        toast.error('Não foi possível carregar o status dos seus jogos');
      }
    };
    
    fetchUserGameStatus();
  }, [user]);

  // Buscar jogos com base na aba ativa
  useEffect(() => {
    const fetchGames = async () => {
      if (!profile) return;
      
      setGamesLoading(true);
      setAllGamesForCurrentTab([]); // Limpa jogos anteriores da aba
      setGames([]); // Limpa jogos visíveis
      
      try {
        let result: UserGame[] = [];
        
        if (activeTab === 'rated') {
          const reviewsData = await getUserReviews(profile.id);
          // Mapear GameReview[] para UserGame[]
          result = reviewsData.map(review => {
            // Construir o objeto Game parcial a partir dos dados da review
            const gameDetails: UserGame['game'] = {
              id: review.game_id,
              name: review.game_name || 'Jogo Desconhecido',
              slug: review.game_slug || review.game_id,
              year: review.game_details?.year || null, 
              image_url: review.game_image_url || null,
              thumb_url: review.game_thumb_url || review.game_image_url || null,
              min_players: review.game_details?.min_players || null,
              max_players: review.game_details?.max_players || null,
              // Campos que NÃO estão em review.game_details ou campos diretos de GameReview serão null/undefined
              ano_nacional: null, 
              description: null, 
              link: null, 
              categories: null, 
              play_time: null, 
              min_age: null, 
              complexity: null, 
              average_rating: null, 
              average_complexity: null, 
              bgg_id: null, 
              ludopedia_id: null, 
              created_at: '', // Estes são do 'Game', não da 'Review' em si.
              updated_at: '', // Estes são do 'Game', não da 'Review' em si.
              mechanics: null, 
              themes: null, 
              artists: null, 
              designers: null, 
              store_products: undefined,
              stats: undefined,
            };

            return {
              id: review.id, 
              user_id: review.user_id,
              game_id: review.game_id,
              notes: review.review_text, 
              rating: review.rating !== undefined ? review.rating : null,
              created_at: review.created_at, 
              updated_at: review.updated_at, 
              game: gameDetails,
              game_slug: gameDetails.slug === null ? undefined : gameDetails.slug, // Explícita conversão de null para undefined
              owned: null, 
              wishlist: null,
              play_count: 0, 
              complexity_rating: null,
            };
          });
        } else {
          switch (activeTab) {
            case 'collection':
              result = await communityService.getUserCollection(profile.id);
              break;
            case 'wishlist':
              result = await communityService.getUserWishlist(profile.id);
              break;
            case 'played':
              result = await communityService.getUserPlayedGames(profile.id);
              break;
            // 'rated' já foi tratado acima
          }
        }
        
        setAllGamesForCurrentTab(result);
        setGames(result.slice(0, INITIAL_GAMES_LIMIT));
        setVisibleGamesCount(INITIAL_GAMES_LIMIT);
        setHasMore(result.length > INITIAL_GAMES_LIMIT);
      } catch (error) {
        console.error(`Erro ao buscar ${activeTab}:`, error);
        toast.error(`Erro ao carregar ${getTabTitle(activeTab)}`);
        setGames([]);
        setAllGamesForCurrentTab([]);
        setHasMore(false);
      } finally {
        setGamesLoading(false);
      }
    };
    
    fetchGames();
  }, [profile, activeTab]);

  const loadMoreGames = () => { // Removido async pois agora opera sobre dados já carregados
    if (gamesLoading || !hasMore || !allGamesForCurrentTab.length) return;
    
    setGamesLoading(true); // Apenas para feedback visual, poderia ser opcional
    
    const newVisibleCount = Math.min(visibleGamesCount + GAMES_LOAD_MORE_INCREMENT, allGamesForCurrentTab.length);
    
    setGames(allGamesForCurrentTab.slice(0, newVisibleCount));
    setVisibleGamesCount(newVisibleCount);
    setHasMore(allGamesForCurrentTab.length > newVisibleCount);
    
    // Simula um pequeno delay para o feedback de loading, pode ser removido se desnecessário
    setTimeout(() => {
      setGamesLoading(false);
    }, 300); 
  };

  const handleSendConnectionRequest = async () => {
    if (!user || !profile) {
      toast.error('Você precisa estar logado para adicionar amigos');
      return;
    }
    
    setActionLoading(true);
    
    try {
      const result = await communityService.sendConnectionRequest(user.id, profile.id);
      
      if (result.success) {
        toast.success('Solicitação de amizade enviada!');
        // Atualizar status da conexão
        const connection = await communityService.getConnectionStatus(user.id, profile.id);
        setConnectionStatus(connection);
      } else {
        toast.error(result.error || 'Erro ao enviar solicitação');
      }
    } catch (error) {
      console.error('Erro ao enviar solicitação:', error);
      toast.error('Erro ao enviar solicitação de amizade');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelRequest = async () => {
    if (!connectionStatus || !user) return;
    
    setActionLoading(true);
    
    try {
      const result = await communityService.removeConnection(connectionStatus.id, user.id);
      
      if (result.success) {
        toast.success('Solicitação cancelada');
        setConnectionStatus(null);
      } else {
        toast.error(result.error || 'Erro ao cancelar solicitação');
      }
    } catch (error) {
      console.error('Erro ao cancelar solicitação:', error);
      toast.error('Erro ao cancelar solicitação');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAcceptRequest = async () => {
    if (!connectionStatus || !user) return;
    
    setActionLoading(true);
    
    try {
      const result = await communityService.updateConnectionStatus(
        connectionStatus.id, 
        'accepted', 
        user.id
      );
      
      if (result.success) {
        toast.success('Solicitação aceita');
        // Atualizar status da conexão
        const connection = await communityService.getConnectionStatus(
          user.id, 
          connectionStatus.user_id === user.id 
            ? connectionStatus.connected_user_id 
            : connectionStatus.user_id
        );
        setConnectionStatus(connection);
      } else {
        toast.error(result.error || 'Erro ao aceitar solicitação');
      }
    } catch (error) {
      console.error('Erro ao aceitar solicitação:', error);
      toast.error('Erro ao aceitar solicitação');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectRequest = async () => {
    if (!connectionStatus || !user) return;
    
    setActionLoading(true);
    
    try {
      const result = await communityService.updateConnectionStatus(
        connectionStatus.id, 
        'rejected', 
        user.id
      );
      
      if (result.success) {
        toast.success('Solicitação rejeitada');
        setConnectionStatus(null);
      } else {
        toast.error(result.error || 'Erro ao rejeitar solicitação');
      }
    } catch (error) {
      console.error('Erro ao rejeitar solicitação:', error);
      toast.error('Erro ao rejeitar solicitação');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendMessage = () => {
    if (!profile) return;
    
    // Redirecionar para a página de mensagens com este usuário
    navigate(`/messages/${profile.username}`);
  };

  const getTabTitle = (tab: ProfileTabType): string => {
    switch (tab) {
      case 'collection': return 'Coleção';
      case 'wishlist': return 'Lista de Desejos';
      case 'played': return 'Jogos Jogados';
      case 'rated': return 'Jogos Avaliados';
    }
  };

  const renderConnectionButton = () => {
    if (!user || !profile || user.id === profile.id) return null;
    
    if (!connectionStatus) {
      return (
        <button
          onClick={handleSendConnectionRequest}
          disabled={actionLoading}
          className={`bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center justify-center w-full sm:w-auto ${theme === 'dark' ? 'border border-gray-700' : ''}`}
        >
          {actionLoading ? (
            <Spinner size="small" />
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
              </svg>
              Adicionar Amigo
            </>
          )}
        </button>
      );
    }
    
    if (connectionStatus.status === 'pending') {
      // Se este usuário é o que enviou a solicitação
      if (connectionStatus.user_id === user.id) {
        return (
          <button
            onClick={handleCancelRequest}
            disabled={actionLoading}
            className={`bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-md flex items-center justify-center w-full sm:w-auto ${theme === 'dark' ? 'border border-gray-700' : ''}`}
          >
            {actionLoading ? (
              <Spinner size="small" />
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                </svg>
                Cancelar Solicitação
              </>
            )}
          </button>
        );
      } 
      // Se este usuário recebeu a solicitação
      else {
        return (
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
            <button
              onClick={handleAcceptRequest}
              disabled={actionLoading}
              className={`bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md flex items-center justify-center w-full sm:w-auto ${theme === 'dark' ? 'border border-gray-700' : ''}`}
            >
              {actionLoading ? (
                <Spinner size="small" />
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Aceitar
                </>
              )}
            </button>
            <button
              onClick={handleRejectRequest}
              disabled={actionLoading}
              className={`bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md flex items-center justify-center w-full sm:w-auto ${theme === 'dark' ? 'border border-gray-700' : ''}`}
            >
              {actionLoading ? (
                <Spinner size="small" />
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  Recusar
                </>
              )}
            </button>
          </div>
        );
      }
    }
    
    if (connectionStatus.status === 'accepted') {
      return (
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
          <button
            onClick={handleSendMessage}
            className={`bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md flex items-center justify-center w-full sm:w-auto ${theme === 'dark' ? 'border border-gray-700' : ''}`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
            </svg>
            Enviar Mensagem
          </button>
          <button
            onClick={handleCancelRequest}
            disabled={actionLoading}
            className={`bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md flex items-center justify-center w-full sm:w-auto ${theme === 'dark' ? 'border border-gray-700' : ''}`}
          >
            {actionLoading ? (
              <Spinner size="small" />
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M11 6a3 3 0 11-6 0 3 3 0 016 0zM14 17a6 6 0 00-12 0h12zM13 8a1 1 0 100 2h4a1 1 0 100-2h-4z" />
                </svg>
                Remover Amigo
              </>
            )}
          </button>
        </div>
      );
    }
    
    return null;
  };

  // Limpar os logs de debug após verificar
  useEffect(() => {
    // Função para limpar os logs de debug após 5 segundos
    const debugTimeout = setTimeout(() => {
      console.clear();
    }, 5000);
    
    return () => clearTimeout(debugTimeout);
  }, [games]);

  // Função para salvar a bio editada
  const handleSaveBio = async () => {
    if (!profile || !user || !isOwnProfile) return;
    
    const originalBio = profile.bio || '';
    const trimmedNewBio = newBio.trim();

    if (trimmedNewBio === originalBio.trim()) {
      setIsEditingBio(false); // A bio não mudou, apenas fecha a edição
      return;
    }

    setIsSavingBio(true);
    setIsModeratingBio(true);
    const moderationToastId = toast.loading('Analisando texto da bio...');

    try {
      const { data: moderationResult, error: moderationInvokeError } = await supabase.functions.invoke('moderate-text', {
        body: { textContent: trimmedNewBio }
      });
      toast.dismiss(moderationToastId);

      if (moderationInvokeError) {
        console.error('[PublicProfilePage] Erro ao invocar função de moderação de texto para bio:', moderationInvokeError);
        toast.error('Falha ao verificar o texto da bio. Tente novamente.');
        setIsModeratingBio(false);
        setIsSavingBio(false);
        return;
      }

      if (moderationResult && !moderationResult.success) {
        const moderationMessage = moderationResult.error || 'Conteúdo da bio considerado impróprio.';
        console.warn(`[PublicProfilePage] Moderação de texto da bio falhou: ${moderationMessage}`);
        toast.error(moderationMessage);
        setIsModeratingBio(false);
        setIsSavingBio(false);
        return;
      }

      if (moderationResult && moderationResult.success) {
        toast.success(moderationResult.message || 'Texto da bio verificado!');
      }
      // Se a moderação passou (ou não houve erro explícito bloqueante), prosseguir para salvar
      const result = await updateProfile({ id: user.id, bio: trimmedNewBio });
      if (result.success) {
        setProfile(prev => prev ? { ...prev, bio: trimmedNewBio } : null);
        setIsEditingBio(false);
        toast.success('Bio atualizada com sucesso!');
      } else {
        throw new Error(result.error || 'Falha ao atualizar bio');
      }
    } catch (error: any) {
      console.error("Erro ao salvar bio:", error);
      toast.error(`Erro ao salvar bio: ${error.message}`);
    } finally {
      setIsModeratingBio(false);
      setIsSavingBio(false);
    }
  };

  // Função para lidar com a mudança de avatar
  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!profile || !user || !isOwnProfile) return;
    const file = e.target.files?.[0];
    if (!file) return;

    // Limpar input para garantir que o onChange dispare mesmo se selecionar o mesmo arquivo
    if (avatarInputRef.current) {
      avatarInputRef.current.value = '';
    }

    // Validar o tipo do arquivo antes de iniciar o upload
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, selecione um arquivo de imagem válido (JPG, PNG, GIF)', {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored"
      });
      return;
    }

    // Validar o tamanho do arquivo (máximo 2MB)
    const MAX_SIZE = 2 * 1024 * 1024; // 2MB
    if (file.size > MAX_SIZE) {
      toast.error('A imagem é muito grande. O tamanho máximo é 2MB', {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored"
      });
      return;
    }

    setIsUploadingAvatar(true);
    let publicUrl: string | null = null;
    let filePathInBucket: string | null = null;

    try {
      // Upload novo avatar
      const fileName = `avatar_${user.id}_${Date.now()}.${file.name.split('.').pop()}`;
      filePathInBucket = `user_${user.id}/${fileName}`; 
      
      const { url, error: uploadError } = await storageService.uploadImage(
        file,
        'avatars', 
        filePathInBucket 
      );

      if (uploadError) {
        toast.error(`Erro no upload: ${uploadError}`, {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored"
        });
        setIsUploadingAvatar(false); 
        return;
      }

      if (!url) {
        toast.error('Não foi possível obter a URL da imagem após o upload', {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored"
        });
        setIsUploadingAvatar(false); 
        return;
      }

      publicUrl = url; 

      // Moderação de Imagem
      if (!publicUrl || !filePathInBucket) {
        toast.error('Erro interno: URL ou caminho do arquivo não encontrado após upload.');
        setIsUploadingAvatar(false);
        return;
      }
      
      setIsModeratingAvatar(true);
      const moderationToastId = toast.loading('Analisando imagem do avatar...');

      const { data: moderationResult, error: moderationInvokeError } = await supabase.functions.invoke('moderate-image', {
        body: { imageUrl: publicUrl, imagePath: filePathInBucket }
      });

      toast.dismiss(moderationToastId);
      
      if (moderationInvokeError) {
        console.error('[PublicProfilePage] Erro ao invocar função de moderação de imagem:', moderationInvokeError);
        toast.error('Falha ao verificar a imagem. Tente outra imagem.');
        // Tenta deletar a imagem já upada se a moderação falhou na invocação
        if (filePathInBucket) { // Checa se filePathInBucket tem valor
            try {
                await storageService.deleteImageByPath(filePathInBucket, 'avatars'); // Usar deleteImageByPath
                console.log("[PublicProfilePage] Imagem de avatar removida do storage após falha na invocação da moderação.");
            } catch (delErr) {
                console.error("[PublicProfilePage] Erro ao tentar remover imagem do avatar após falha de invocação:", delErr);
            }
        }
        setIsModeratingAvatar(false);
        setIsUploadingAvatar(false);
        return;
      }

      if (moderationResult && !moderationResult.success) {
        const moderationMessage = moderationResult.error || 'Conteúdo da imagem do avatar considerado impróprio.';
        console.warn(`[PublicProfilePage] Moderação de avatar falhou: ${moderationMessage}`);
        toast.error(moderationMessage);
        // A função moderate-image já deve ter deletado a imagem do storage se unsafe
        setIsModeratingAvatar(false);
        setIsUploadingAvatar(false);
        return;
      }
      
      if (moderationResult && moderationResult.success) {
        toast.success(moderationResult.message || 'Imagem do avatar verificada!');
      }
      // Fim da Moderação de Imagem

      // Se a moderação passou, prossiga para atualizar o perfil
      const result = await updateProfile({ id: user.id, avatar_url: publicUrl });
      if (result.success) {
        setProfile(prev => prev ? { ...prev, avatar_url: publicUrl! } : null);
        toast.success('Avatar atualizado com sucesso!', {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored"
        });
      } else {
        // Tentar deletar a imagem recém-uploadada E MODERADA COM SUCESSO em caso de falha na atualização do perfil
        if (filePathInBucket) { // Checa se filePathInBucket tem valor
        try {
            await storageService.deleteImageByPath(filePathInBucket, 'avatars'); // Usar deleteImageByPath
            console.log("[PublicProfilePage] Imagem de avatar removida do storage após falha na atualização do perfil (moderação OK).");
        } catch (deleteError) {
            console.error("[PublicProfilePage] Erro ao excluir imagem de avatar após falha na atualização do perfil:", deleteError);
        }
        }
        toast.error(`Falha ao atualizar perfil: ${result.error || 'Erro desconhecido'}`, {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored"
        });
      }
    } catch (error: any) {
      console.error("Erro ao alterar avatar:", error);
      // Tenta deletar a imagem se publicUrl foi definida e não estamos no meio de uma moderação já (evitar dupla deleção)
      if (publicUrl && filePathInBucket && !isModeratingAvatar) { 
          try {
              await storageService.deleteImageByPath(filePathInBucket, 'avatars'); // Usar deleteImageByPath
              console.log("[PublicProfilePage] Imagem de avatar removida do storage devido a erro geral no handleAvatarChange.");
          } catch (deleteError) {
              console.error("[PublicProfilePage] Erro ao tentar remover imagem de avatar em cleanup de erro geral:", deleteError);
          }
      }
      toast.error(`Erro ao alterar avatar: ${error.message || 'Erro desconhecido'}`, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored"
      });
    } finally {
      setIsUploadingAvatar(false);
      setIsModeratingAvatar(false); 
    }
  };

  useEffect(() => {
    const fetchMascot = async () => {
      if (!profile) return;
      setLoadingMascot(true);
      try {
        const mascot = await getUserMascot(supabase, profile.id, profileStatus?.current_level ?? 1);
        setUserMascot(mascot);
      } catch (err) {
        setUserMascot(null);
      } finally {
        setLoadingMascot(false);
      }
    };
    fetchMascot();
  }, [profile, profileStatus]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)]">
        <Spinner size="large" />
        <p className={`mt-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Carregando perfil...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`container mx-auto px-4 py-10 text-center ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
        <h2 className="text-2xl font-bold mb-4">Erro</h2>
        <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>{error}</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)]">
        <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>Usuário não encontrado</h2>
        <p className={`mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>O perfil que você procura não existe ou foi removido.</p>
        <button
          onClick={() => navigate('/')}
          className={`mt-4 px-4 py-2 rounded-md ${theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
        >
          Voltar para Home
        </button>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title={`Perfil de ${profile?.username || username || 'Usuário'} - Boardverso`}
        description={`Veja a coleção, wishlist e atividades de ${profile?.username || username} na comunidade Boardverso.`}
      />
      <div className={`container mx-auto px-4 py-6 ${theme === 'dark' ? 'text-foreground' : 'bg-gray-100'}`}>
        {/* Cabeçalho do perfil */}
        <div className={`rounded-lg shadow-md p-4 sm:p-6 mb-6 ${theme === 'dark' ? 'bg-gray-800 text-card-foreground border border-border' : 'bg-white'}`}>
          <div className="flex flex-col sm:flex-row sm:items-start">
            <div className="flex-shrink-0 mb-4 sm:mb-0 sm:mr-6 relative text-center sm:text-left">
              {profile.avatar_url ? (
                <img 
                  src={profile.avatar_url} 
                  alt={`Avatar de ${profile.username}`}
                  className={`w-24 h-24 rounded-full object-cover border mx-auto sm:mx-0 ${theme === 'dark' ? 'border-gray-700' : 'border-gray-300'}`}
                />
              ) : (
                <div className={`w-24 h-24 rounded-full flex items-center justify-center border mx-auto sm:mx-0 ${theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-300 border-gray-300'}`}>
                  <span className={`text-2xl font-bold ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    {profile.username?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
              )}
              {isOwnProfile && (
                 <>
                   <input
                     type="file"
                     accept="image/*"
                     ref={avatarInputRef}
                     onChange={handleAvatarChange}
                     className="hidden"
                     disabled={isUploadingAvatar}
                   />
                   <button
                     onClick={() => avatarInputRef.current?.click()}
                     disabled={isUploadingAvatar}
                     className={`absolute -bottom-1 -right-1 sm:right-0 text-white rounded-full p-1.5 shadow-md transition transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2 ${theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500' : 'bg-blue-500 hover:bg-blue-600 focus:ring-blue-500'}`}
                     title="Alterar avatar"
                   >
                     {isUploadingAvatar ? <Spinner size="small" color="text-white" /> : <Upload size={16} />}
                   </button>
                 </>
              )}
            </div>
            
            <div className="flex-grow min-w-0">
              <h1 className={`text-2xl font-bold mb-1 text-center sm:text-left ${theme === 'dark' ? 'text-foreground' : 'text-gray-900'}`}>
                {profile.full_name || profile.username}
              </h1>
              {profile.full_name && (
                <h2 className={`mb-2 text-center sm:text-left ${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-600'}`}>@{profile.username}</h2>
              )}
              <div className={`mb-3 break-words ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                {isEditingBio ? (
                  <div>
                    <textarea
                      value={newBio}
                      onChange={(e) => setNewBio(e.target.value)}
                      className={`w-full p-2 border rounded mt-1 text-sm focus:ring-blue-500 ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400 focus:border-blue-500' : 'border-gray-300 placeholder-gray-400 focus:border-blue-500'}`}
                      rows={3}
                      placeholder="Conte um pouco sobre você..."
                      disabled={isSavingBio}
                    />
                    <div className="flex space-x-2 mt-2">
                      <button 
                        onClick={handleSaveBio} 
                        disabled={isSavingBio} 
                        className={`flex items-center justify-center text-white text-xs px-3 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed ${theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'}`}
                      >
                        {isSavingBio ? <Spinner size="small" color="text-white" /> : <Save size={14} className="mr-1"/>} Salvar
                      </button>
                      <button 
                        onClick={() => setIsEditingBio(false)} 
                        disabled={isSavingBio} 
                        className={`flex items-center justify-center text-xs px-3 py-1 rounded disabled:opacity-50 ${theme === 'dark' ? 'bg-gray-600 hover:bg-gray-500 text-gray-200' : 'bg-gray-300 hover:bg-gray-400 text-gray-700'}`}
                      >
                        <XCircle size={14} className="mr-1"/> Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="inline">
                    {profile.bio || 'Este usuário ainda não possui uma biografia.'}
                    {isOwnProfile && (
                      <button 
                        onClick={() => { setIsEditingBio(true); setNewBio(profile.bio || ''); }} 
                        className={`ml-2 inline-block align-middle ${theme === 'dark' ? 'text-blue-400 hover:text-blue-300' : 'text-blue-500 hover:text-blue-700'}`}
                        title="Editar bio"
                      >
                        <Edit size={16} />
                      </button>
                    )}
                  </p>
                )}
              </div>
              <p className={`text-sm ${theme === 'dark' ? 'text-muted-foreground' : 'text-gray-500'}`}>
                Membro desde {new Date(profile.created_at).toLocaleDateString('pt-BR')}
              </p>

               {/* <<< INÍCIO: Exibição de Gamificação e Badges >>> */}
               <div className={`mt-4 border-t pt-4 ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                 {loadingProfileStatus ? (
                   <>
                     <Skeleton className="h-4 w-1/2 mb-2" />
                     <Skeleton className="h-3 w-full mb-1" />
                     <Skeleton className="h-3 w-3/4" />
                   </>
                 ) : profileStatus ? (
                   <div className="space-y-2">
                     <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between text-sm space-y-1 sm:space-y-0">
                        <div className="flex items-center gap-2">
                          <Star className="h-4 w-4 text-yellow-400" />
                          <span className={`font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>Nível {profileStatus.current_level}</span>
                           <UiBadge variant="secondary" className={`${theme === 'dark' ? 'bg-gray-700 text-gray-300 border-gray-600' : ''}`}>{profileStatus.current_rank?.toUpperCase()}</UiBadge>
                        </div>
                        <div className={`flex items-center gap-2 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-muted-foreground'}`}>
                            {profileStatus.rank_position !== null && (
                                <span title="Posição no ranking global">
                                    <Hash className="inline-block h-3 w-3 mr-0.5" />
                                    #{profileStatus.rank_position}
                                </span>
                            )}
                        </div>
                     </div>
                      <div>
                         <Progress 
                            value={calculateLevelProgress(profileStatus.total_xp, profileStatus.current_level)} 
                            className={`h-2 w-full ${theme === 'dark' ? '[&>div]:bg-primary' : ''}`} 
                         />
                         <div className="flex justify-end mt-1">
                           <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-muted-foreground'}`}>{profileStatus.total_xp} XP</span>
                         </div>
                      </div>
                      {/* Mascote do usuário */}
                      {userMascot && !loadingMascot && (
                        <div className={`flex items-center gap-4 border rounded-lg p-3 mt-2 ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-gray-50'}`}>
                          <img src={userMascot.current_level.image_url} alt={userMascot.mascot.name} className={`w-14 h-14 rounded-full object-cover border ${theme === 'dark' ? 'border-gray-600' : ''}`} />
                          <div>
                            <div className={`font-semibold text-lg ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>{userMascot.mascot.name}</div>
                            <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-muted-foreground'}`}>Nível {userMascot.current_level.level_number}</div>
                          </div>
                        </div>
                      )}
                   </div>
                 ) : (
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-muted-foreground'}`}>Informações de nível não disponíveis.</p>
                 )}

                 {/* <<< Exibição das Badges Públicas >>> */}
                 <div className="mt-3">
                   {loadingPublicBadges ? (
                     <div className="flex space-x-2">
                       <Skeleton className="h-10 w-10 rounded-full" />
                       <Skeleton className="h-8 w-8 rounded-full" />
                       <Skeleton className="h-8 w-8 rounded-full" />
                       <Skeleton className="h-8 w-8 rounded-full" />
                     </div>
                   ) : publicBadges.length > 0 ? (
                     <TooltipProvider delayDuration={100}>
                       <div className="flex flex-wrap items-end gap-2">
                         {publicBadges.map((badge) => (
                           <Tooltip key={badge.id}>
                             <TooltipTrigger asChild>
                               <img
                                 src={badge.icon_url || './placeholder-badge.png'} // Fallback image
                                 alt={badge.name}
                                 className={`rounded-full border-2 w-8 h-8 object-cover shadow-sm hover:scale-110 transition-transform cursor-help ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`} 
                               />
                             </TooltipTrigger>
                             <TooltipContent side="bottom" className={`p-2 rounded text-xs max-w-xs shadow-lg ${theme === 'dark' ? 'bg-gray-900 text-gray-200 border border-gray-700' : 'bg-gray-800 text-white'}`}>
                               <p className="font-bold">{badge.name}</p>
                               <p>{badge.description}</p>
                               {badge.unlocked_at && <p className={`mt-1 text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>Desbloqueada em: {new Date(badge.unlocked_at).toLocaleDateString('pt-BR')}</p>}
                             </TooltipContent>
                           </Tooltip>
                         ))}
                       </div>
                     </TooltipProvider>
                   ) : (
                     <p className={`text-xs italic ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>Nenhuma badge destacada.</p>
                   )}
                 </div>
               </div>
               {/* <<< FIM: Exibição de Gamificação e Badges >>> */}
              
              {/* Botão Histórico/Badges (Apenas para o próprio usuário) */}
              {isOwnProfile && (
                <div className="mt-4">
                  <Button variant="outline" size="sm" asChild className={`${theme === 'dark' ? 'border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-gray-200' : ''}`}>
                    <Link to="/profile/xp-history" className="flex items-center">
                      <BarChart className="h-4 w-4 md:mr-2" /> 
                      <span className="hidden md:inline">Meu Progresso</span>
                    </Link>
                  </Button>
                </div>
              )}
            </div>
            
            <div className="mt-4 sm:mt-0 sm:ml-4 flex-shrink-0 w-full sm:w-auto">
              {renderConnectionButton()}
            </div>
          </div>
        </div>
        
        {/* Feed Atividade Recente */}
        {profile && (
            <div className={`mt-8 mb-8 pt-6 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                <h2 className={`text-xl font-semibold mb-4 ${theme === 'dark' ? 'text-foreground' : 'text-gray-800'}`}>Atividade Recente de {profile.username}</h2>
                <FeedComponent 
                    key={`${profile.id}-feed`}
                    externalFilters={{ filterUserId: profile.id }} 
                    limit={3}
                    showCreatePostForm={false} 
                    onTagClick={handlePublicFeedTagClick} 
                />
            </div>
        )}

        {/* Abas para diferentes listas de jogos */}
        <div className="mb-6">
          <div className={`border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
            <nav className="flex flex-wrap -mb-px">
              {(['collection', 'wishlist', 'played', 'rated'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-3 sm:px-5 py-2 border-b-2 font-medium text-sm whitespace-nowrap ${ 
                    activeTab === tab
                      ? theme === 'dark' ? 'border-blue-500 text-blue-400' : 'border-blue-500 text-blue-600'
                      : theme === 'dark' ? 'border-transparent text-gray-500 hover:text-gray-400 hover:border-gray-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {getTabTitle(tab)}
                </button>
              ))}
            </nav>
          </div>
        </div>
        
        {/* Lista de jogos */}
        <div className="mt-6">
          {gamesLoading && games.length === 0 ? (
            <div className="flex flex-col items-center py-10">
              <Spinner size="large" />
              <p className={`mt-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Carregando jogos...</p>
            </div>
          ) : games.length > 0 ? (
            <>
              <div className="space-y-4">
                {games.map((userGame) => {
                  if (!userGame.game) {
                    // Adiciona um log para jogos sem dados completos e pula a renderização
                    console.warn(`[PublicProfilePage] Jogo com ID ${userGame.id} (game_id: ${userGame.game_id}) não possui dados de 'game' e não será renderizado.`);
                    return null;
                  }
                  const gameId = userGame.game.id;
                  const gameSlug = userGame.game.slug || gameId;

                  const isOwnedByViewer = user ? userGameStatusIds.ownedIds.has(gameId) : false;
                  const isWishlistByViewer = user ? userGameStatusIds.wishlistIds.has(gameId) : false;
                  const isPlayedByViewer = user ? userGameStatusIds.playedIds.has(gameId) : false;
                  
                  // Ação de avaliação simplificada (exemplo, pode precisar de um modal/popover)
                  const simpleRateAction = (newRating: number) => {
                    if (!user) {
                      toast.error("Você precisa estar logado para avaliar.");
                      return;
                    }
                    handleRateGame(gameId, newRating);
                  };
                  
                  return (
                    <div 
                      key={userGame.id} 
                      className={`flex flex-col sm:flex-row items-center p-3 space-x-0 sm:space-x-4 rounded-lg shadow hover:shadow-lg transition-shadow duration-200 ${
                        theme === 'dark' ? 'bg-gray-800 border border-gray-700 hover:border-gray-600' : 'bg-white border border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Link to={`/games/titulo/${gameSlug}`} className="flex-shrink-0 w-full sm:w-auto mb-3 sm:mb-0 flex justify-center sm:justify-start">
                        <img 
                          src={userGame.game.thumb_url || userGame.game.image_url || '/placeholder-game.png'} 
                          alt={userGame.game.name} 
                          className="w-20 h-28 sm:w-16 sm:h-20 object-cover rounded-md mx-auto sm:mx-0" 
                        />
                      </Link>
                      
                      <div className="flex-grow min-w-0 w-full sm:w-auto flex flex-col items-center sm:items-start text-center sm:text-left">
                        <Link to={`/games/titulo/${gameSlug}`} className={`hover:underline ${theme === 'dark' ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}>
                          <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>{userGame.game.name}</h3>
                        </Link>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} flex flex-col sm:flex-row items-center sm:justify-center sm:items-start sm:justify-start`}>
                          {userGame.game.year}
                          {userGame.game.min_players && userGame.game.max_players && (
                            <span className="sm:ml-1 sm:before:content-['|'] sm:before:mx-1">{`${userGame.game.min_players}${userGame.game.min_players !== userGame.game.max_players ? '-' + userGame.game.max_players : ''} Jogadores`}</span>
                          )}
                        </p>
                        {userGame.rating && (
                          <div className="flex items-center justify-center sm:justify-start mt-1">
                            {[1,2,3,4,5].map(star => (
                              <Star 
                                key={star} 
                                size={16} 
                                className={star <= userGame.rating! ? (theme === 'dark' ? 'text-yellow-400 fill-yellow-400' : 'text-yellow-500 fill-yellow-500') : (theme === 'dark' ? 'text-gray-600' : 'text-gray-300')} 
                              />
                            ))}
                            <span className={`ml-1 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>({userGame.rating})</span>
                          </div>
                        )}
                        {/* Exibir texto da avaliação e data para a aba 'rated' */}
                        {activeTab === 'rated' && userGame.notes && (
                          <div className={`mt-2 text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>
                            <p className="italic">"{userGame.notes}"</p>
                            <p className={`mt-1 text-xxs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                              Avaliado em: {new Date(userGame.updated_at).toLocaleDateString('pt-BR')}
                            </p>
                          </div>
                        )}
                        {/* Exibir play_count se aplicável na aba */}
                        {(activeTab === 'played' || (activeTab === 'collection' && typeof userGame.play_count === 'number' && userGame.play_count > 0)) && typeof userGame.play_count === 'number' && userGame.play_count > 0 && (
                           <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                             Jogou {userGame.play_count} vez{userGame.play_count > 1 ? 'es' : ''}
                           </p>
                        )}
                      </div>
                      
                      {/* Botões de Ação */}
                      {user && (
                        <div className="flex flex-wrap justify-center sm:justify-end items-center space-x-1 sm:space-x-2 mt-3 sm:mt-0 w-full sm:w-auto">
                          <TooltipProvider delayDuration={200}>
                            {/* Botão Coleção */}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleToggleOwned(gameId, !isOwnedByViewer)}
                                  className={`rounded-full ${theme === 'dark' ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
                                  disabled={actionLoading}
                                >
                                  <BookOpen size={20} className={isOwnedByViewer ? (theme === 'dark' ? 'text-green-400' : 'text-green-500') : ''} />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="top" className={`p-1.5 text-xs rounded shadow-md ${theme === 'dark' ? 'bg-gray-900 text-gray-200 border border-gray-700' : 'bg-gray-800 text-white'}`}>
                                {isOwnedByViewer ? 'Remover da Coleção' : 'Adicionar à Coleção'}
                              </TooltipContent>
                            </Tooltip>

                            {/* Botão Wishlist */}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleToggleWishlist(gameId, !isWishlistByViewer)}
                                  className={`rounded-full ${theme === 'dark' ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
                                  disabled={actionLoading}
                                >
                                  <Heart size={20} className={isWishlistByViewer ? 'text-red-500 fill-red-500' : ''} />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="top" className={`p-1.5 text-xs rounded shadow-md ${theme === 'dark' ? 'bg-gray-900 text-gray-200 border border-gray-700' : 'bg-gray-800 text-white'}`}>
                                {isWishlistByViewer ? 'Remover da Wishlist' : 'Adicionar à Wishlist'}
                              </TooltipContent>
                            </Tooltip>

                            {/* Botão Jogado */}
                             <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleTogglePlayed(gameId, !isPlayedByViewer)}
                                  className={`rounded-full ${theme === 'dark' ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
                                  disabled={actionLoading}
                                >
                                  <PlusCircle size={20} className={isPlayedByViewer ? (theme === 'dark' ? 'text-blue-400' : 'text-blue-500') : ''} />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="top" className={`p-1.5 text-xs rounded shadow-md ${theme === 'dark' ? 'bg-gray-900 text-gray-200 border border-gray-700' : 'bg-gray-800 text-white'}`}>
                                {isPlayedByViewer ? 'Desmarcar como Jogado' : 'Marcar como Jogado'}
                              </TooltipContent>
                            </Tooltip>

                          </TooltipProvider>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {/* Botão para carregar mais */}
              {hasMore && (
                <div className="mt-8 flex justify-center">
                  <button
                    onClick={loadMoreGames}
                    disabled={gamesLoading}
                    className={`px-6 py-2 rounded-md flex items-center ${theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                  >
                    {gamesLoading ? (
                      <>
                        <Spinner size="small" />
                        <span className="ml-2">Carregando...</span>
                      </>
                    ) : (
                      'Carregar mais'
                    )}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className={`text-center py-10 border rounded-lg ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-gray-50'}`}>
              <h3 className={`text-xl font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Nenhum jogo encontrado</h3>
              <p className={`mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                {profile.username} ainda não adicionou jogos a {getTabTitle(activeTab).toLowerCase()}.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Adiciona o componente ToastContainer para exibir as notificações */}
      <ToastContainer 
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={theme === 'dark' ? 'dark' : 'light'} // <<< Ajustar tema do ToastContainer
      />
    </>
  );
};

export default PublicProfilePage; 